import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const analyticsApi = axios.create({
  baseURL: `${API_BASE_URL}/analytics`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const reportsApi = axios.create({
  baseURL: `${API_BASE_URL}/reports`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor
analyticsApi.interceptors.request.use((config) => {
  const noAuthEndpoints = ['/login/', '/auth-version/'];
  const needsAuth = !noAuthEndpoints.some(endpoint => config.url.includes(endpoint));

  if (needsAuth) {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Basic ${token}`;
    }
  }
  return config;
}, (error) => Promise.reject(error));

// Apply same interceptors to reports API
reportsApi.interceptors.request.use((config) => {
  const noAuthEndpoints = [];
  const needsAuth = !noAuthEndpoints.some(endpoint => config.url.includes(endpoint));
  if (needsAuth) {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Basic ${token}`;
    }
  }
  return config;
}, (error) => Promise.reject(error));

// The 1-hour session window is an INACTIVITY timeout, not an absolute one:
// any successful authenticated call slides it forward. Before this, the
// clock started at login and never moved — an actively-working user was
// dumped to the login page on their first refresh past the hour (Apal
// 2026-09-25: 'i keep getting logged off').
const touchSession = (response) => {
  if (localStorage.getItem('adminToken')) {
    localStorage.setItem('sessionStartTime', Date.now().toString());
  }
  return response;
};

reportsApi.interceptors.response.use(
  touchSession,
  (error) => {
    if (error.response && error.response.status === 401 && !error.config.url.includes('/login/')) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUsername');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Handle 401 responses
analyticsApi.interceptors.response.use(
  touchSession,
  (error) => {
    if (error.response && error.response.status === 401 && !error.config.url.includes('/login/')) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUsername');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth functions
export const login = async (username, password) => {
  try {
    const response = await analyticsApi.post('/login/', { username, password });
    return response.data;
  } catch (error) {
    if (error.response) return error.response.data;
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminUsername');
  localStorage.removeItem('adminRole');
  localStorage.removeItem('isAdmin');
  localStorage.removeItem('allowedMarkets');
  localStorage.removeItem('allowedPages');
  localStorage.removeItem('allowedSymbols');
  localStorage.removeItem('authVersion');
  localStorage.removeItem('sessionStartTime');
  sessionStorage.removeItem('tabActive');
  window.location.href = '/login';
};

const SESSION_TIMEOUT_MS = 60 * 60 * 1000;

export const isSessionExpired = () => {
  const sessionStartTime = localStorage.getItem('sessionStartTime');
  if (!sessionStartTime) return false;
  return Date.now() - parseInt(sessionStartTime, 10) > SESSION_TIMEOUT_MS;
};

export const isNewTabAfterClose = () => {
  const hasToken = !!localStorage.getItem('adminToken');
  const tabWasActive = !!sessionStorage.getItem('tabActive');
  return hasToken && !tabWasActive;
};

export const initSessionTracking = () => {
  if (isSessionExpired()) {
    logout();
    return false;
  }
  if (isNewTabAfterClose()) {
    logout();
    return false;
  }
  if (localStorage.getItem('adminToken')) {
    sessionStorage.setItem('tabActive', 'true');
  }
  return true;
};

export const setSessionStartTime = () => {
  localStorage.setItem('sessionStartTime', Date.now().toString());
  sessionStorage.setItem('tabActive', 'true');
};

export const checkAuthVersion = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/analytics/auth-version/`);
    if (response.ok) {
      const data = await response.json();
      const serverVersion = data.auth_version?.toString();
      const storedVersion = localStorage.getItem('authVersion');
      if (localStorage.getItem('adminToken') && storedVersion !== serverVersion) {
        logout();
        return false;
      }
    }
    return true;
  } catch (error) {
    return true;
  }
};

export const refreshUserPermissions = async () => {
  const token = localStorage.getItem('adminToken');
  if (!token) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/analytics/me/`, {
      headers: { 'Authorization': `Basic ${token}` },
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('adminRole', data.role || 'viewer');
      localStorage.setItem('isAdmin', JSON.stringify(data.is_admin || false));
      localStorage.setItem('allowedMarkets', JSON.stringify(data.allowed_markets || ['all']));
      localStorage.setItem('allowedPages', JSON.stringify(data.allowed_pages || ['all']));
      localStorage.setItem('allowedSymbols', JSON.stringify(data.allowed_symbols || {}));
      localStorage.setItem('authVersion', data.auth_version?.toString() || '1');
      localStorage.setItem('hasShorts', JSON.stringify(data.has_shorts || false));
      localStorage.setItem('shortsSymbols', JSON.stringify(data.shorts_symbols || []));
      localStorage.setItem('permittedSymbols', JSON.stringify(data.permitted_symbols || []));
      return true;
    } else if (response.status === 401) {
      logout();
      return false;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const isAuthenticated = () => !!localStorage.getItem('adminToken');
export const getUserRole = () => localStorage.getItem('adminRole') || 'viewer';
export const isAdmin = () => {
  const stored = localStorage.getItem('isAdmin');
  return stored ? JSON.parse(stored) : false;
};

export const getAllowedMarkets = () => {
  const markets = localStorage.getItem('allowedMarkets');
  return markets ? JSON.parse(markets) : [];
};

export const getAllowedPages = () => {
  const pages = localStorage.getItem('allowedPages');
  return pages ? JSON.parse(pages) : [];
};

export const hasShorts = () => {
  try { return JSON.parse(localStorage.getItem('hasShorts')) || false; } catch { return false; }
};

export const getShortsSymbols = () => {
  try { return JSON.parse(localStorage.getItem('shortsSymbols')) || []; } catch { return []; }
};

// Symbols the user holds a UserSymbolPermission row for.
// EMPTY = no rows assigned = sees everything (same rule as the backend).
export const getPermittedSymbols = () => {
  try { return JSON.parse(localStorage.getItem('permittedSymbols')) || []; } catch { return []; }
};

// One-shot cached L+S verdict from THE central universe endpoint
// (data capability ∩ instrument tradability ∩ this user's shorts grant).
// Every selector's canShort reads this — never a local list.
let _universeShortsPromise = null;
export const loadUniverseShorts = () => {
  if (!_universeShortsPromise) {
    _universeShortsPromise = apiService.getUniverse().then(res =>
      new Set((res.data?.regions || []).flatMap(r =>
        r.symbols.filter(s => s.shorts).map(s => s.name)))
    ).catch(err => { _universeShortsPromise = null; throw err; });
  }
  return _universeShortsPromise;
};

export const hasPageAccess = (page) => {
  const allowed = getAllowedPages();
  if (allowed.includes('all')) return true;
  return allowed.includes(page.toLowerCase());
};

export const hasMarketAccess = (market) => {
  const allowed = getAllowedMarkets();
  if (allowed.includes('all')) return true;
  return allowed.includes(market.toLowerCase());
};

export const apiService = {
  // Dashboard
  getDashboardStats: () => analyticsApi.get('/dashboard/stats/'),
  // Cache-bust so a manual Refresh always re-fetches fresh push/lock state (never a cached GET).
  getPollingStatus: () => analyticsApi.get('/polling-status/', { params: { _t: Date.now() } }),
  getClosePriceValidation: (params = {}) => analyticsApi.get('/close-price-validation/', { params }),
  getIndiaCloseVerification: () => analyticsApi.get('/india-close-verification/', { params: { _t: Date.now() } }),
  getConnectorHealth: (params = {}) => analyticsApi.get('/connector-health/', { params: { _t: Date.now(), ...params } }),
  getLockStatus: () => analyticsApi.get('/lock-status/', { params: { _t: Date.now() } }),
  getScoreOverview: () => analyticsApi.get('/score-overview/'),
  getPollingLogs: (params = {}) => analyticsApi.get('/polling-logs/', { params }),
  getMarketHealth: (params = {}, config = {}) => analyticsApi.get('/market-health/', { params, ...config }),
  getSignalHistory: (params = {}, config = {}) => analyticsApi.get('/signal-history/', { params, ...config }),
  getNotificationHistory: (params = {}, config = {}) => analyticsApi.get('/notification-history/', { params: { _t: Date.now(), ...params }, ...config }),

  // Markets & Regions
  getMarkets: () => analyticsApi.get('/markets/'),
  getRegions: () => analyticsApi.get('/regions/'),

  // Settings - Users
  getUsersList: (params = {}) => analyticsApi.get('/users-list/', { params }),
  toggleUserStatus: (userId) => analyticsApi.post(`/users/${userId}/toggle-status/`),

  // Settings - Symbols
  getSymbols: () => analyticsApi.get('/symbols/'),
  toggleSymbolStatus: (symbolId) => analyticsApi.post(`/symbols/${symbolId}/toggle-status/`),

  // Settings - User Permissions
  getUserPermissions: (userId) => analyticsApi.get(`/user-permissions/${userId}/`),
  setUserPermissions: (userId, symbolIds, shortsSymbolIds = []) => analyticsApi.post(`/user-permissions/${userId}/`, { symbol_ids: symbolIds, shorts_symbol_ids: shortsSymbolIds }),

  // Reports
  getAvailableReports: () => reportsApi.get('/available/'),
  // config: { signal } lets a page cancel a request a newer filter click replaced
  getAllTransactions: (params = {}, config = {}) => reportsApi.get('/all-transactions/', { params, ...config }),
  getYearlyStatistics: (params = {}, config = {}) => reportsApi.get('/yearly-statistics/', { params, ...config }),
  getPerformanceSummary: (params = {}, config = {}) => reportsApi.get('/performance-summary/', { params, ...config }),
  getAllIndexesSummary: (params = {}, config = {}) => reportsApi.get('/all-indexes-summary/', { params, ...config }),
  getPeriodPerformance: (params = {}, config = {}) => reportsApi.get('/period-performance/', { params, ...config }),
  getSignalAnalysis: (params = {}, config = {}) => reportsApi.get('/signal-analysis/', { params, ...config }),
  getMultiClosePrices: (params = {}, config = {}) => reportsApi.get('/multi-close-prices/', { params, ...config }),
  downloadReport: (symbol) => reportsApi.get('/download/', { params: { symbol }, responseType: 'blob' }),
  getOHLCExport: (symbol) => reportsApi.get('/ohlc-export/', { params: { symbol } }),

  // Published vs Latest comparison ("did the model change")
  getModelDiff: (params = {}, config = {}) => reportsApi.get('/model-diff/', { params, ...config }),
  // per-user review of Today's Health issues ("Mark reviewed" / "Reopen")
  getIssueReviews: () => analyticsApi.get('/issue-reviews/'),
  postIssueReview: (body) => analyticsApi.post('/issue-reviews/', body),
  evaluateAllocation: (body = {}, config = {}) => reportsApi.post('/allocation-evaluate/', body, config),
  // THE central selector source: user's visible symbols + per-symbol L+S
  // verdict (data capability ∩ tradability ∩ shorts grant). Every index
  // selector reads this — no page hardcodes an index list or shorts flag.
  getUniverse: (params = {}) => analyticsApi.get('/universe/', { params }),

  // Indexes
  getScoreHistory: (params = {}, config = {}) => analyticsApi.get('/score-history/', { params, ...config }),
};

export { analyticsApi, reportsApi };
export default analyticsApi;
